import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useId, useRef, useState } from 'react';
import { MONTHS_FULL } from './civil-date.js';
import { cn } from './cn.js';
import { FloatingPanel } from './floating-panel.js';

const MONTHS_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

export interface MonthPickerProps {
  label?: string | undefined;
  value: string | null;
  onValueChange: (value: string | null) => void;
  emptyLabel?: string | undefined;
  className?: string | undefined;
  /// `toolbar` é o seletor da barra de período, sem rótulo visível. `field` é o campo de
  /// formulário: rótulo à vista e controle com a mesma moldura dos demais campos.
  variant?: 'toolbar' | 'field' | undefined;
  hint?: string | undefined;
  /// Some com o botão que limpa a escolha, para o campo que exige um mês.
  clearable?: boolean | undefined;
}

/// Seletor estilizado de mês/ano: navega por ano e escolhe o mês numa grade, sem recorrer ao
/// `<input type="month">` nativo. Espelha o controle consolidado no sistema legado.
export function MonthPicker({
  label,
  value,
  onValueChange,
  emptyLabel = 'Todos os períodos',
  className,
  variant = 'toolbar',
  hint,
  clearable = true,
}: MonthPickerProps) {
  const field = variant === 'field';
  const id = useId();
  const selected = parseMonth(value);
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(selected.year);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);

  const display = value ? `${MONTHS_FULL[selected.month]} de ${String(selected.year)}` : emptyLabel;
  return (
    <div className={cn('relative min-w-0', field && 'space-y-2', className)}>
      {label ? (
        <span
          id={`${id}-label`}
          className={field ? 'block text-xs font-semibold text-content/70' : 'sr-only'}
        >
          {label}
        </span>
      ) : null}
      <button
        type="button"
        ref={trigger}
        aria-labelledby={label ? `${id}-label ${id}-value` : undefined}
        aria-label={label ? undefined : 'Escolher mês'}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!open) setYear(selected.year);
          setOpen((current) => !current);
        }}
        className={cn(
          'flex cursor-pointer items-center gap-2 text-sm text-content transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
          field
            ? 'h-12 w-full justify-between rounded-xl border border-surface-border bg-surface-elevated px-4 hover:border-brand-500'
            : 'min-h-10 min-w-44 justify-center rounded-lg px-3 font-semibold hover:bg-content/5',
        )}
      >
        <span id={`${id}-value`} className="truncate">
          {display}
        </span>
        <Calendar className="size-4 shrink-0 text-brand-400" />
      </button>
      {open ? (
        <FloatingPanel
          anchorRef={trigger}
          onDismiss={close}
          align="center"
          role="dialog"
          aria-label="Escolher mês e ano"
          className="w-[17.5rem] rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card"
        >
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setYear((current) => current - 1)}
              aria-label="Ano anterior"
              className="grid size-9 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <ChevronLeft className="size-4" />
            </button>
            <strong aria-live="polite" className="text-sm">
              {year}
            </strong>
            <button
              type="button"
              onClick={() => setYear((current) => current + 1)}
              aria-label="Próximo ano"
              className="grid size-9 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {MONTHS_SHORT.map((month, index) => {
              const candidate = `${String(year)}-${String(index + 1).padStart(2, '0')}`;
              const active = candidate === value;
              return (
                <button
                  key={month}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    onValueChange(candidate);
                    setOpen(false);
                  }}
                  className={cn(
                    'h-10 cursor-pointer rounded-lg text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
                    active
                      ? 'bg-brand-500 font-semibold text-on-brand'
                      : 'text-content-secondary hover:bg-content/5 hover:text-content',
                  )}
                >
                  {month}
                </button>
              );
            })}
          </div>
          {clearable ? (
            <button
              type="button"
              onClick={() => {
                onValueChange(null);
                setOpen(false);
              }}
              className="mt-2 min-h-9 w-full cursor-pointer rounded-lg border-t border-surface-border px-3 pt-2 text-xs font-semibold text-content-muted transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              {emptyLabel}
            </button>
          ) : null}
        </FloatingPanel>
      ) : null}
      {field && hint ? <p className="text-xs leading-5 text-content-muted">{hint}</p> : null}
    </div>
  );
}

function parseMonth(value: string | null): { year: number; month: number } {
  const now = new Date();
  if (!value) return { year: now.getFullYear(), month: now.getMonth() };
  const [year, month] = value.split('-').map(Number) as [number, number];
  return { year, month: month - 1 };
}
